import { Observable, tap } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { computed, Injectable, signal } from '@angular/core';
import { OptionResponse, OptionType } from '@models/options-response.model';
import { PasstypeResponse } from '@models/passtype/passtype.model';
import { PostalCodeResponse } from '@models/postal-code-reponse.model';
import {
    AddressRespone, ChangeContactResponse, NewRemitter, RegisterResponse, Remitter
} from '@models/remitter/remitter.model';

@Injectable({
  providedIn: 'root'
})
export class RemitterService {
  private readonly _remitterInfo = signal<Remitter | null>(null);
  remitterInfo = computed(() => this._remitterInfo());
  
  constructor(private httpClient: HttpClient) {}

  updateRemitterInfo = (value: any) => {
    this._remitterInfo.set(value);
  };

  getPassTypeRequest = {
    country: ''
  };

  getPassTypeCountries(method: string ='new'): Observable<any> {
    return this.httpClient.get<any>('passtype/countries', {params: { method: method }});
  }

  getPasTypeByCountry(country: string) {
    return this.httpClient.get<PasstypeResponse>('passtype', { params: { country: country } });
  }

  getAddress(postalCode: string): Observable<AddressRespone> {
    return this.httpClient.get<AddressRespone>('misc/get-address/' + postalCode);
  }

  getPostalCodeAddress(postalCode: string) {
    return this.httpClient.get<PostalCodeResponse>('misc/get-address/' + postalCode);
  }

  getOptions(type: OptionType) {
    return this.httpClient.get<OptionResponse>(
      `misc/predefined-options?page=1&per_page=10000&type=${type}`
    );
  }

  getRemitterProfile(is_required_translate: boolean = false) {
    return this.httpClient.get<Remitter>('remitter', { params: { is_required_translate } });
  }

  checkIdExpiry(): Observable<any> {
    return this.httpClient.get<any>('remitter/check-id-expiry');
  }

  verifyContact = (contactType: string) => {
    return this.httpClient.post<ChangeContactResponse>('remitter/verify-existing-contact', {
      purpose: 'verify_existing_contact',
      change_on: contactType
    });
  };

  confirmNewContact = (contact: string, changeType: string, otpUUID: string) => {
    const newContactParam = 'new_' + changeType;
    return this.httpClient.post<ChangeContactResponse>('remitter/confirm-new-contact', {
      purpose: 'update_new_contact',
      change_on: changeType,
      [newContactParam]: contact,
      otp_purpose_uuid: otpUUID
    });
  };

  updateInfo = (input: Record<string, any>) => {
    input['source_of_funds'] = input['source_of_funds'].split(',');
    // if (!input['is_resident']) {
    //   delete input['city'];
    // }
    return this.httpClient.put<Remitter>('remitter', input);
  };

  updatePDPAConsent = (consent: { email: boolean; sms: boolean; phone: boolean }) => {
    return this.httpClient.put<Remitter>('remitter/update-pdpa-consent', { pdpa_consent: consent });
  };

  readonly register = {
    new: (remitterData: NewRemitter): Observable<RegisterResponse> =>
      this.httpClient.post<RegisterResponse>('remitter', remitterData).pipe(
        tap(response => {
          localStorage.setItem('access_token', response.access_token);
        })
      )
  };

  uploadKyc(request: FormData): Observable<any> {
    console.log('request', request);
    return this.httpClient.post<any>('remitter/kyc/upload', request, {
      params: { notEncrypt: true }
    });
  }

  kycCheck(): Observable<any> {
    return this.httpClient.post<any>('remitter/kyc/check', null);
  }

  validateIdNo(country: string, id_type: string, id_no: string) {
    return this.httpClient.post<{ valid: boolean }>('remitter/validate-id-no', {
      id_type: id_type,
      country: country,
      id_no
    });
  }

  validateUnique(type: 'username' | 'email' | 'id_no' | 'mobile', value: string) {
    return this.httpClient.post<null>('remitter/validate-unique-input', { [type]: value });
  }

  validateMultipleUnique(input: Partial<Record<'username' | 'email' | 'id_no' | 'mobile', string>>) {
    return this.httpClient.post<null>('remitter/validate-unique-input', input);
  }

  convertNric(input: { id_no: string; id_type: string }) {
    return this.httpClient.put<Remitter>('remitter/convert-pass', input);
  }
}
